import type { Translations } from '@/shared/i18n'
import type { FaqItem, HowToStep } from '@/shared/seo/schema'
import type { SeoRouteConfig } from '@saas-pages/shared/public/seo'

export type { FaqItem, HowToStep }

export type UtilityStatus = 'available' | 'coming-soon'
export type UtilityCategory = 'pdf' | 'office' | 'image'
export type UtilityTaskCategory =
  | 'compression'
  | 'pdf-editing'
  | 'pdf-conversion'
  | 'image-pdf'
  | 'security'

export interface UtilityCard {
  key: UtilityKey
  path: string
  analyticsKey: string
  category: UtilityCategory
  taskCategory: UtilityTaskCategory
  title: string
  shortDescription: string
  longDescription: string
  marketingParagraphs: string[]
  icon: string
  status: UtilityStatus
  faq: FaqItem[]
  howTo: HowToStep[]
}

export interface UtilityDefinition {
  key: string
  path: string
  analyticsKey: string
  category: UtilityCategory
  taskCategory: UtilityTaskCategory
  icon: string
  status: UtilityStatus
  seo: SeoRouteConfig
}

export const UTILITY_DEFINITIONS = [
  {
    key: 'pdf-viewer',
    path: '/otkryt-pdf',
    analyticsKey: 'pdf-viewer',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: 'PDF',
    status: 'available',
    seo: {
      path: '/otkryt-pdf',
      title: 'Открыть PDF онлайн бесплатно — просмотр в браузере',
      description:
        'Откройте и просмотрите страницы PDF локально в браузере. Навигация, масштаб и сведения о файле без загрузки документа на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'sign-pdf',
    path: '/podpisat-pdf',
    analyticsKey: 'sign-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '✍️',
    status: 'available',
    seo: {
      path: '/podpisat-pdf',
      title: 'Подписать PDF онлайн бесплатно — визуальная подпись',
      description: 'Добавьте нарисованную, напечатанную или загруженную визуальную подпись на выбранные страницы PDF локально в браузере. Не КЭП.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'povernut',
    path: '/povernut',
    analyticsKey: 'rotate',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '🔄',
    status: 'available',
    seo: {
      path: '/povernut',
      title: 'Повернуть PDF онлайн бесплатно — pdfy.ru',
      description:
        'Поверните страницы PDF на 90°, 180° или 270°. Локально в браузере, без загрузки на сервер. Все страницы или выбранный диапазон.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'vJpg',
    path: '/v-jpg',
    analyticsKey: 'pdf-to-jpg',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: '🖼️',
    status: 'available',
    seo: {
      path: '/v-jpg',
      title: 'PDF в JPG онлайн бесплатно — pdfy.ru',
      description:
        'Конвертируйте PDF в JPG-картинки локально в браузере. Каждая страница — отдельный JPG, многостраничный документ — ZIP. Файл не уходит на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'pdf-to-png',
    path: '/pdf-v-png',
    analyticsKey: 'pdf-to-png',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: 'PNG',
    status: 'available',
    seo: {
      path: '/pdf-v-png',
      title: 'PDF в PNG онлайн бесплатно — pdfy.ru',
      description:
        'Конвертируйте PDF в PNG-картинки локально в браузере. Каждая страница — отдельный PNG, многостраничный документ — ZIP. Файл не уходит на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'jpgVPdf',
    path: '/jpg-v-pdf',
    analyticsKey: 'jpg-v-pdf',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: '🖼️',
    status: 'available',
    seo: {
      path: '/jpg-v-pdf',
      title: 'JPG в PDF онлайн бесплатно — pdfy.ru',
      description:
        'Соберите JPG и PNG изображения в один PDF локально в браузере. Бесплатно, без регистрации, без водяных знаков и без загрузки файлов на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'document-scanner',
    path: '/skaner-dokumentov-online',
    analyticsKey: 'document-scanner',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: '▣',
    status: 'available',
    seo: {
      path: '/skaner-dokumentov-online',
      title: 'Сканер документов онлайн — фото в PDF бесплатно | pdfy',
      description:
        'Обрежьте, поверните и улучшите фото страниц, затем скачайте один PDF. Локально в браузере, без OCR, регистрации и водяных знаков.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'png-v-pdf',
    path: '/png-v-pdf',
    analyticsKey: 'png-v-pdf',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: 'PNG',
    status: 'available',
    seo: {
      path: '/png-v-pdf',
      title: 'PNG в PDF онлайн бесплатно — pdfy.ru',
      description:
        'Соберите PNG изображения в один PDF локально в браузере. JPG тоже поддерживается как дополнительный формат. Бесплатно, без регистрации и без загрузки файлов на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'heicVPdf',
    path: '/heic-v-pdf',
    analyticsKey: 'heic-v-pdf',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: 'HEIC',
    status: 'available',
    seo: {
      path: '/heic-v-pdf',
      title: 'HEIC в PDF онлайн бесплатно — pdfy.ru',
      description:
        'Конвертируйте HEIC и HEIF фотографии в PDF локально в браузере. Бесплатно, без регистрации, без водяных знаков и без загрузки файлов на сервер.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
  {
    key: 'htmlToPdf',
    path: '/html-v-pdf',
    analyticsKey: 'html-to-pdf',
    category: 'pdf',
    taskCategory: 'pdf-conversion',
    icon: 'HTML',
    status: 'available',
    seo: {
      path: '/html-v-pdf',
      title: 'HTML в PDF онлайн бесплатно — pdfy.ru',
      description:
        'Преобразуйте вставленный HTML или HTML-файл в PDF локально в браузере. Бесплатно, без регистрации и без загрузки HTML на сервер.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
  {
    key: 'pdfToText',
    path: '/pdf-v-text',
    analyticsKey: 'pdf-to-text',
    category: 'pdf',
    taskCategory: 'pdf-conversion',
    icon: '¶',
    status: 'available',
    seo: {
      path: '/pdf-v-text',
      title: 'PDF в текст онлайн бесплатно — pdfy.ru',
      description:
        'Извлеките текстовый слой из PDF в TXT локально в браузере. Без OCR: сканы и картинки не распознаются.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'compare-pdf',
    path: '/sravnit-pdf',
    analyticsKey: 'compare-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '≠',
    status: 'available',
    seo: {
      path: '/sravnit-pdf',
      title: 'Сравнить PDF онлайн бесплатно — pdfy.ru',
      description:
        'Сравните два PDF по текстовому слою локально в браузере. Показываем изменённые, добавленные и удалённые страницы без загрузки файлов на сервер.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
  {
    key: 'extract-images-from-pdf',
    path: '/izvlech-kartinki-iz-pdf',
    analyticsKey: 'extract-pdf-images',
    category: 'image',
    taskCategory: 'image-pdf',
    icon: 'IMG',
    status: 'available',
    seo: {
      path: '/izvlech-kartinki-iz-pdf',
      title: 'Извлечь картинки из PDF онлайн бесплатно — pdfy.ru',
      description:
        'Достаньте встроенные растровые изображения из PDF локально в браузере. PDF не загружается на сервер, найденные картинки можно скачать по одной или ZIP-архивом.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
  {
    key: 'flatten-pdf',
    path: '/flatten-pdf',
    analyticsKey: 'flatten-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '▣',
    status: 'available',
    seo: {
      path: '/flatten-pdf',
      title: 'Зафиксировать поля PDF онлайн (flatten) — pdfy.ru',
      description:
        'Сделайте поля PDF-формы нередактируемыми локально в браузере. Значения останутся видимыми, файл не загружается на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'fill-pdf-form',
    path: '/zapolnit-pdf-formu',
    analyticsKey: 'fill-pdf-form',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '✎',
    status: 'available',
    seo: {
      path: '/zapolnit-pdf-formu',
      title: 'Заполнить PDF-форму онлайн бесплатно — локально | pdfy',
      description:
        'Заполните текст, флажки, радиокнопки и списки существующей AcroForm PDF-формы в браузере. Скачайте обычную или зафиксированную копию без отправки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'pdf-metadata',
    path: '/metadannye-pdf',
    analyticsKey: 'pdf-metadata',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: 'ⓘ',
    status: 'available',
    seo: {
      path: '/metadannye-pdf',
      title: 'Проверить метаданные PDF онлайн — посмотреть и изменить',
      description:
        'Посмотрите метаданные PDF онлайн: автор, название, программа, даты создания и изменения. Исправьте их бесплатно, файл не загружается на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'redact-pdf',
    path: '/zamazat-tekst-v-pdf',
    analyticsKey: 'redact-pdf',
    category: 'pdf',
    taskCategory: 'security',
    icon: '█',
    status: 'available',
    seo: {
      path: '/zamazat-tekst-v-pdf',
      title: 'Замазать текст в PDF онлайн — локальная redaction',
      description:
        'Закройте фрагменты PDF чёрными прямоугольниками и скачайте локально пересобранный image-only PDF. Текстовый слой и ссылки удаляются при растеризации страниц.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'unlockPdf',
    path: '/snyat-parol-s-pdf',
    analyticsKey: 'unlock-pdf',
    category: 'pdf',
    taskCategory: 'security',
    icon: '🔓',
    status: 'available',
    seo: {
      path: '/snyat-parol-s-pdf',
      title: 'Снять пароль с PDF по известному паролю — pdfy.ru',
      description:
        'Удалите пароль открытия с PDF по известному паролю локально в браузере. Без взлома, без восстановления забытых паролей и без загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'repair-pdf',
    path: '/vosstanovit-pdf',
    analyticsKey: 'repair-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: 'PDF',
    status: 'available',
    seo: {
      path: '/vosstanovit-pdf',
      title: 'Восстановить PDF онлайн бесплатно — pdfy.ru',
      description:
        'Попробуйте восстановить структуру PDF локально в браузере через QPDF/WASM: переписываем xref/trailer и сохраняем новую копию без загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'szhat',
    path: '/szhat',
    analyticsKey: 'compress',
    category: 'pdf',
    taskCategory: 'compression',
    icon: '🗜️',
    status: 'available',
    seo: {
      path: '/szhat',
      title: 'Сжать PDF онлайн до нужного размера — pdfy.ru',
      description:
        'Сожмите PDF онлайн бесплатно или выберите ориентир 5 МБ. Сразу покажем, достигнут ли размер. Без регистрации и загрузки на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'excelVPdf',
    path: '/excel-v-pdf',
    analyticsKey: 'from-excel',
    category: 'office',
    taskCategory: 'pdf-conversion',
    icon: '📊',
    status: 'available',
    seo: {
      path: '/excel-v-pdf',
      title: 'Excel в PDF онлайн бесплатно — pdfy.ru',
      description:
        'Конвертируйте XLSX, XLS, ODS в PDF прямо в браузере. Каждый лист становится страницами PDF. Без регистрации, водяных знаков и загрузки на сервер.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'mergePdf',
    path: '/obedinit-pdf',
    analyticsKey: 'merge-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '📄',
    status: 'available',
    seo: {
      path: '/obedinit-pdf',
      title: 'Объединить PDF онлайн бесплатно — pdfy.ru',
      description:
        'Соедините несколько PDF в один документ локально в браузере. Можно менять порядок файлов. Бесплатно, без регистрации.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'splitPdf',
    path: '/razdelit-pdf',
    analyticsKey: 'split-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '✂️',
    status: 'available',
    seo: {
      path: '/razdelit-pdf',
      title: 'Разделить PDF онлайн бесплатно — pdfy.ru',
      description:
        'Извлеките нужные страницы из PDF в новый файл локально в браузере. Диапазоны вида 1-3, 7, 10-12. Бесплатно, без регистрации.',
      changefreq: 'monthly',
      priority: 0.8,
    },
  },
  {
    key: 'crop-pdf',
    path: '/obrezat-pdf',
    analyticsKey: 'crop-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '✂️',
    status: 'available',
    seo: {
      path: '/obrezat-pdf',
      title: 'Обрезать PDF онлайн бесплатно — pdfy.ru',
      description:
        'Обрежьте поля PDF локально в браузере: задайте отступы или перетащите рамку предпросмотра. Бесплатно, без регистрации и загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
  {
    key: 'numeraciyaStranic',
    path: '/numeraciya-stranic-pdf',
    analyticsKey: 'number-pdf-pages',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '🔢',
    status: 'available',
    seo: {
      path: '/numeraciya-stranic-pdf',
      title: 'Нумерация страниц в PDF онлайн — pdfy.ru',
      description:
        'Добавьте номера страниц в PDF: выберите позицию, стартовый номер и страницу начала нумерации. Бесплатно, локально в браузере, без регистрации.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'watermark-pdf',
    path: '/vodyanoj-znak-pdf',
    analyticsKey: 'watermark-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '💧',
    status: 'available',
    seo: {
      path: '/vodyanoj-znak-pdf',
      title: 'Водяной знак на PDF онлайн — pdfy.ru',
      description:
        'Добавьте свой текстовый водяной знак на все страницы PDF локально в браузере. Настройте прозрачность, позицию и угол. Без регистрации.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'protect-pdf',
    path: '/postavit-parol-na-pdf',
    analyticsKey: 'protect-pdf',
    category: 'pdf',
    taskCategory: 'security',
    icon: '🔐',
    status: 'available',
    seo: {
      path: '/postavit-parol-na-pdf',
      title: 'Поставить пароль на PDF онлайн — pdfy.ru',
      description:
        'Защитите PDF паролем открытия локально в браузере. Бесплатно, без регистрации и без загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'deletePdfPages',
    path: '/udalit-stranicy-iz-pdf',
    analyticsKey: 'delete-pdf-pages',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '✂️',
    status: 'available',
    seo: {
      path: '/udalit-stranicy-iz-pdf',
      title: 'Удалить страницы из PDF онлайн бесплатно — pdfy.ru',
      description:
        'Удалите лишние страницы из PDF по номерам или диапазонам. Бесплатно, локально в браузере, без загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'reorder-pdf-pages',
    path: '/pomenyat-poryadok-stranic-pdf',
    analyticsKey: 'reorder-pdf-pages',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '↕',
    status: 'available',
    seo: {
      path: '/pomenyat-poryadok-stranic-pdf',
      title: 'Поменять порядок страниц PDF онлайн — pdfy.ru',
      description:
        'Переставьте страницы PDF в нужном порядке локально в браузере. Бесплатно, без регистрации и без загрузки файла на сервер.',
      changefreq: 'monthly',
      priority: 0.7,
    },
  },
  {
    key: 'n-up-pdf',
    path: '/neskolko-stranits-pdf-na-liste',
    analyticsKey: 'n-up-pdf',
    category: 'pdf',
    taskCategory: 'pdf-editing',
    icon: '▦',
    status: 'available',
    seo: {
      path: '/neskolko-stranits-pdf-na-liste',
      title: 'Несколько страниц PDF на одном листе — 2, 4 или 8',
      description:
        'Разместите 2, 4 или 8 страниц PDF на листе A4 или A3. Настройте ориентацию, поля и интервалы, затем скачайте готовый PDF для печати.',
      changefreq: 'monthly',
      priority: 0.75,
    },
  },
] as const satisfies readonly UtilityDefinition[]

export type UtilityKey = (typeof UTILITY_DEFINITIONS)[number]['key']

export type UtilityDefinitionFor<TKey extends UtilityKey = UtilityKey> = Extract<
  (typeof UTILITY_DEFINITIONS)[number],
  { key: TKey }
>

/** Build the localised list of utilities for the current translation tree. */
export function buildUtilities(t: Translations): UtilityCard[] {
  return UTILITY_DEFINITIONS.map((s) => {
    const meta = t.utilities[s.key]
    return {
      ...s,
      title: meta.title,
      shortDescription: meta.shortDescription,
      longDescription: meta.longDescription,
      marketingParagraphs: [...meta.marketing],
      faq: [...meta.faq],
      howTo: [...meta.howTo],
    }
  })
}

/** Stable list of utility keys (for callers that only need structure, e.g. routes). */
export const UTILITY_KEYS: readonly UtilityKey[] = UTILITY_DEFINITIONS.map((u) => u.key)

export function getUtilityDefinition<TKey extends UtilityKey>(
  key: TKey,
): UtilityDefinitionFor<TKey> {
  const utility = UTILITY_DEFINITIONS.find((u) => u.key === key)
  if (!utility) {
    throw new Error(`[utility-registry] Unknown utility key: "${key}"`)
  }
  return utility as UtilityDefinitionFor<TKey>
}
